import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';

const AdvancedSearchModal = ({ isOpen, onClose, onFiltersApplied }) => {
  const { searchFilters, setSearchFilters, contentLanguage, isDebugMode, t } = useAppContext();
  
  // Local state for form values
  const [localFilters, setLocalFilters] = useState({
    yearFrom: searchFilters.yearFrom || '',
    yearTo: searchFilters.yearTo || '',
    titleLanguage: searchFilters.titleLanguage || 'all',
    descriptionLanguage: searchFilters.descriptionLanguage || (contentLanguage !== 'all' ? contentLanguage : 'all')
  });

  // Update local state when searchFilters or contentLanguage change
  useEffect(() => {
    setLocalFilters(prevFilters => ({
      ...prevFilters,
      yearFrom: searchFilters.yearFrom || '',
      yearTo: searchFilters.yearTo || '',
      titleLanguage: searchFilters.titleLanguage || 'all',
      descriptionLanguage: searchFilters.descriptionLanguage || (contentLanguage !== 'all' ? contentLanguage : 'all')
    }));
  }, [searchFilters, contentLanguage]);

  // Rescan on modal open to ensure current user preference is applied
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(prevFilters => ({
        ...prevFilters,
        yearFrom: searchFilters.yearFrom || '',
        yearTo: searchFilters.yearTo || '',
        titleLanguage: searchFilters.titleLanguage || 'all',
        descriptionLanguage: searchFilters.descriptionLanguage || (contentLanguage !== 'all' ? contentLanguage : 'all')
      }));
    }
  }, [isOpen, searchFilters, contentLanguage]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Only update if there are actual changes to prevent unnecessary API calls
    const hasChanges = Object.keys(localFilters).some(key => 
      localFilters[key] !== (searchFilters[key] || (key.includes('Language') ? 'all' : ''))
    );
    
    if (hasChanges) {
      setSearchFilters(prev => ({
        ...prev,
        ...localFilters
      }));
      
      // Notify parent component that filters were applied
      if (onFiltersApplied) {
        onFiltersApplied(localFilters);
      }
    }
    
    onClose();
  };

  // Handle form reset
  const handleReset = () => {
    setLocalFilters({
      yearFrom: '',
      yearTo: '',
      titleLanguage: 'all',
      descriptionLanguage: contentLanguage !== 'all' ? contentLanguage : 'all'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">{t('advancedSearch.title')}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          {/* Date Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('advancedSearch.dateRange')}</label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="yearFrom"
                placeholder={t('advancedSearch.fromYear')}
                className="w-1/2 px-3 py-2 border border-gray-300 rounded text-sm"
                value={localFilters.yearFrom}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="yearTo"
                placeholder={t('advancedSearch.toYear')}
                className="w-1/2 px-3 py-2 border border-gray-300 rounded text-sm"
                value={localFilters.yearTo}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          {/* Title Language */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('advancedSearch.titleLanguage')}</label>
            <select
              name="titleLanguage"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              value={localFilters.titleLanguage}
              onChange={handleInputChange}
            >
              <option value="all">{t('advancedSearch.allLanguages')}</option>
              <option value="en">{t('advancedSearch.english')}</option>
              <option value="fr">{t('advancedSearch.french')}</option>
              <option value="other">{t('advancedSearch.other')}</option>
            </select>
          </div>
          
          {/* Description Language */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('advancedSearch.descriptionLanguage')}</label>
            <select
              name="descriptionLanguage"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              value={localFilters.descriptionLanguage}
              onChange={handleInputChange}
            >
              <option value="all">{t('advancedSearch.allLanguages')}</option>
              <option value="en">{t('advancedSearch.english')}</option>
              <option value="fr">{t('advancedSearch.french')}</option>
              <option value="other">{t('advancedSearch.other')}</option>
            </select>
          </div>
          
          
          {/* Buttons */}
          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('advancedSearch.reset')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              {t('advancedSearch.applyFilters')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;
